import React, { useState, useMemo, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useProject } from "../contexts/ProjectContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { ArrowLeft, Plus, FileText, Trash2, Edit, Settings, Feather, Search } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { projects, prompts, deletePrompt, updateProject } = useProject();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState({ name: "", description: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [updateButtonState, setUpdateButtonState] = useState("idle"); // idle, updating, updated

  const project = projects.find(p => p.id === projectId);
  const projectPrompts = prompts[projectId] || [];

  // Check if user is pro/unlocked (unlimited prompts per project)
  const isProUser = user?.subscription_status === 'active' || user?.unlocked_with_code === true;

  // Reset update button state when user makes changes to the edit form
  useEffect(() => {
    if (isEditDialogOpen && updateButtonState === "updated") {
      const hasChanges = editProject.name !== project?.name || editProject.description !== (project?.description || "");
      if (hasChanges) {
        setUpdateButtonState("idle");
      }
    }
  }, [editProject, project, isEditDialogOpen, updateButtonState]);

  // Filter and sort prompts based on search query and updated date
  const filteredPrompts = useMemo(() => {
    let prompts = projectPrompts;
    
    // Filter by search query if provided
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      prompts = prompts.filter(prompt => 
        prompt.title.toLowerCase().includes(query) ||
        prompt.content.toLowerCase().includes(query)
      );
    }
    
    // Sort by updated_at date, newest first
    return prompts.sort((a, b) => {
      const dateA = new Date(a.updated_at);
      const dateB = new Date(b.updated_at);
      return dateB - dateA; // Newest first (descending order)
    });
  }, [projectPrompts, searchQuery]);

  if (!project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Project not found</h2>
          <Link to="/dashboard">
            <Button>Return to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleDeletePrompt = async (promptId, promptTitle) => {
    if (window.confirm(`Are you sure you want to delete "${promptTitle}"?`)) {
      try {
        await deletePrompt(projectId, promptId);
        toast({
          title: "Deleted",
          description: "Prompt deleted successfully"
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete prompt",
          variant: "destructive"
        });
      }
    }
  };

  const handleUpdateProject = async () => {
    if (!editProject.name.trim()) {
      toast({
        title: "Error",
        description: "Project name is required",
        variant: "destructive"
      });
      return;
    }

    setUpdateButtonState("updating");
    try {
      await updateProject(projectId, editProject);
      setUpdateButtonState("updated");
      
      // Show success state briefly
      setTimeout(() => {
        setIsEditDialogOpen(false);
        setUpdateButtonState("idle");
      }, 1000);
      
      toast({
        title: "Success", 
        description: "Project updated successfully"
      });
    } catch (error) {
      setUpdateButtonState("idle");
      toast({
        title: "Error",
        description: "Failed to update project",
        variant: "destructive"
      });
    }
  };

  const openEditDialog = () => {
    setEditProject({ name: project.name, description: project.description || "" });
    setUpdateButtonState("idle"); // Reset button state when opening dialog
    setIsEditDialogOpen(true);
  };

  const truncateContent = (content, maxLength = 120) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Feather className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-800">{project.name}</h1>
                  <p className="text-sm text-slate-500">{projectPrompts.length} prompts</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={openEditDialog}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Project
              </Button>
              <Link to="/settings">
                <Button variant="ghost" size="sm">
                  <Settings className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800">Prompts</h2>
              <p className="text-slate-600 mt-1">
                {project.description || "No description provided"}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Search Box */}
              {projectPrompts.length > 0 && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder="Search prompts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              )}
              <Link to={`/project/${projectId}/prompt`}>
                <Button
                  className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
                  disabled={!isProUser && projectPrompts.length >= 20}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Prompt {!isProUser && projectPrompts.length >= 20 && "(Max 20)"}
                </Button>
              </Link>
            </div>
          </div>

          {/* Search Results Info */}
          {searchQuery && (
            <div className="mb-4 text-sm text-slate-600">
              Found {filteredPrompts.length} prompt{filteredPrompts.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </div>
          )}

          {filteredPrompts.length === 0 ? (
            <div className="text-center py-16">
              {searchQuery ? (
                <>
                  <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h4 className="text-xl font-medium text-slate-500 mb-2">No prompts found</h4>
                  <p className="text-slate-400 mb-6">No prompts match your search "{searchQuery}"</p>
                  <Button onClick={() => setSearchQuery("")} variant="outline">
                    Clear Search
                  </Button>
                </>
              ) : (
                <>
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h4 className="text-xl font-medium text-slate-500 mb-2">No prompts yet</h4>
                  <p className="text-slate-400 mb-6">Create your first prompt to get started</p>
                  <Link to={`/project/${projectId}/prompt`}>
                    <Button className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Prompt
                    </Button>
                  </Link>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredPrompts.map((prompt) => (
                <Link key={prompt.id} to={`/project/${projectId}/prompt/${prompt.id}`} className="block">
                  <Card className="group hover:shadow-lg transition-all duration-200 border-slate-200 hover:border-violet-300 h-48 flex flex-col cursor-pointer">
                    <CardHeader className="pb-2 flex-shrink-0">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-sm font-semibold text-slate-800 group-hover:text-violet-600 transition-colors line-clamp-2">
                          {prompt.title}
                        </CardTitle>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeletePrompt(prompt.id, prompt.title);
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 flex-shrink-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 flex-1 flex flex-col">
                      <div className="flex-1 mb-3">
                        <p className="text-xs text-slate-600 line-clamp-4">
                          {truncateContent(prompt.content)}
                        </p>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-400 mb-3">
                        <span>Updated {new Date(prompt.updated_at).toLocaleDateString()}</span>
                      </div>
                      <div className="mt-auto flex items-center text-xs text-violet-600 group-hover:text-violet-700 transition-colors">
                        <Edit className="w-3 h-3 mr-1" />
                        Click to edit prompt
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Edit Project Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Project</DialogTitle>
              <DialogDescription>
                Update your project name and description.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Project Name</Label>
                <Input
                  id="edit-name"
                  value={editProject.name}
                  onChange={(e) => setEditProject({ ...editProject, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editProject.description}
                  onChange={(e) => setEditProject({ ...editProject, description: e.target.value })}
                />
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdateProject}
                  disabled={updateButtonState !== "idle"}
                  className={updateButtonState === "updated" ? "bg-green-600 hover:bg-green-700" : ""}
                >
                  {updateButtonState === "updating" && "Updating..."}
                  {updateButtonState === "updated" && "Updated!"}
                  {updateButtonState === "idle" && "Update Project"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ProjectDetail;